/**
 * Self-hosted auth API client.
 * In production, this talks to a Cloudflare Worker + D1 database backend.
 * The API_URL is configured via REACT_APP_AUTH_API_URL env var.
 */

const AUTH_API_URL =
  process.env.REACT_APP_AUTH_API_URL ?? 'http://localhost:8787/auth'

export interface AuthUser {
  id: string
  email: string
  displayName: string | null
  photoURL: string | null
  provider: 'google' | 'microsoft' | 'email'
  createdAt: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

export interface LoginResponse {
  user: AuthUser
  tokens: AuthTokens
}

const getHeaders = (token?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/**
 * Initiate Google OAuth - returns the URL to redirect to
 */
export const getGoogleAuthUrl = (): string => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''
  const redirectUri = `${window.location.origin}/auth/callback/google`
  const scope = encodeURIComponent('openid email profile')
  return (
    `https://accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code&scope=${scope}&access_type=offline&prompt=consent`
  )
}

/**
 * Initiate Microsoft OAuth - returns the URL to redirect to
 */
export const getMicrosoftAuthUrl = (): string => {
  const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID ?? ''
  const redirectUri = `${window.location.origin}/auth/callback/microsoft`
  const scope = encodeURIComponent('openid email profile User.Read')
  return (
    `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
    `client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code&scope=${scope}`
  )
}

/**
 * Exchange OAuth code for tokens via our self-hosted backend
 */
export const exchangeOAuthCode = async (
  provider: 'google' | 'microsoft',
  code: string
): Promise<LoginResponse> => {
  const response = await fetch(`${AUTH_API_URL}/oauth/${provider}/callback`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      code,
      redirectUri: `${window.location.origin}/auth/callback/${provider}`
    })
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Auth failed: ${error}`)
  }

  return response.json() as Promise<LoginResponse>
}

/**
 * Get current user profile using stored access token
 */
export const getCurrentUser = async (
  accessToken: string
): Promise<AuthUser> => {
  const response = await fetch(`${AUTH_API_URL}/me`, {
    method: 'GET',
    headers: getHeaders(accessToken)
  })

  if (!response.ok) {
    throw new Error('Failed to get user profile')
  }

  return response.json() as Promise<AuthUser>
}

/**
 * Refresh the access token using refresh token
 */
export const refreshAccessToken = async (
  refreshToken: string
): Promise<AuthTokens> => {
  const response = await fetch(`${AUTH_API_URL}/refresh`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ refreshToken })
  })

  if (!response.ok) {
    throw new Error('Token refresh failed')
  }

  return response.json() as Promise<AuthTokens>
}

/**
 * Sign out - invalidate tokens server-side
 */
export const signOutApi = async (accessToken: string): Promise<void> => {
  await fetch(`${AUTH_API_URL}/signout`, {
    method: 'POST',
    headers: getHeaders(accessToken)
  }).catch(() => {
    // Sign out locally even if server request fails
  })
}
